import type { ProofOfDeliveryItemType } from "sagas/types/detailTransactions";

export const PROOF_IMAGE_TYPES = ["PHOTO", "SIGN"] as const;

const PROOF_KEYS = ["proofOfDelivery", "proof_of_delivery"] as const;

function getProofRaw(obj: unknown): Record<string, unknown> | null {
  if (!obj || typeof obj !== "object") return null;
  const o = obj as Record<string, unknown>;
  const raw = o[PROOF_KEYS[0]] ?? o[PROOF_KEYS[1]];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

function pick<T>(record: Record<string, unknown> | null | undefined, ...keys: string[]): T | undefined {
  if (!record) return undefined;
  for (const k of keys) {
    const v = record[k];
    if (v != null && v !== "") return v as T;
  }
  return undefined;
}

export function getProofFromDetail(dt: Record<string, unknown>): Record<string, unknown> | null {
  const fromRoot = getProofRaw(dt);
  if (fromRoot) return fromRoot;
  const tx = dt.transaction as Record<string, unknown> | undefined;
  const fromTx = getProofRaw(tx);
  if (fromTx) return fromTx;
  const history = (dt.transactionHistory ?? []) as unknown[];
  const deliveredStep = [...history]
    .reverse()
    .find((trx: unknown) => (trx as Record<string, unknown>)?.reason === "state_delivered") as Record<string, unknown> | undefined;
  return getProofRaw(deliveredStep);
}

export function getProofOfDeliveryArray(obj: unknown): ProofOfDeliveryItemType[] {
  if (!obj || typeof obj !== "object") return [];
  const raw = (obj as Record<string, unknown>)[PROOF_KEYS[0]] ?? (obj as Record<string, unknown>)[PROOF_KEYS[1]];
  if (!raw || typeof raw !== "object") return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item: unknown) => {
        if (!item || typeof item !== "object") return null;
        const o = item as Record<string, unknown>;
        const type = (o.type ?? o.Type) as string | undefined;
        const value = (o.value ?? o.Value) as string | undefined;
        if (type != null && value != null)
          return { type: String(type).toUpperCase(), value: String(value) };
        return null;
      })
      .filter((x): x is ProofOfDeliveryItemType => x !== null);
  }
  const pod = raw as Record<string, unknown>;
  const photo = pod.photo != null && pod.photo !== "" ? String(pod.photo) : null;
  const sign = pod.sign != null && pod.sign !== "" ? String(pod.sign) : null;
  const items: ProofOfDeliveryItemType[] = [];
  if (photo) items.push({ type: "PHOTO", value: photo });
  if (sign) items.push({ type: "SIGN", value: sign });
  return items;
}

export function getDeliveryCoords(dt: Record<string, unknown>): {
  lat: string;
  lng: string;
  finishedByAdmin: boolean;
  timestamp?: Date;
} {
  const pod = getProofFromDetail(dt);
  const deliveryProof = dt.deliveryProof as Record<string, unknown> | undefined;
  const finishedByAdmin = Boolean(
    pick<boolean>(pod ?? undefined, "finishByAdmin", "finish_by_admin") ??
    deliveryProof?.finishedByAdmin
  );
  const latRaw = pick<string | number>(pod ?? undefined, "latitude", "lat") ?? deliveryProof?.latitude;
  const lngRaw = pick<string | number>(pod ?? undefined, "longitude", "lng") ?? deliveryProof?.longitude;
  const lat = latRaw != null && latRaw !== "" ? String(latRaw) : "";
  const lng = lngRaw != null && lngRaw !== "" ? String(lngRaw) : "";
  const rawTs = pick<unknown>(pod ?? undefined, "timestamp") ?? deliveryProof?.timestamp;
  const timestamp =
    rawTs instanceof Date && !Number.isNaN(rawTs.getTime())
      ? rawTs
      : typeof rawTs === "string"
        ? (() => {
            const d = new Date(rawTs);
            return !Number.isNaN(d.getTime()) ? d : undefined;
          })()
        : undefined;
  return { finishedByAdmin, lat, lng, timestamp };
}

export type ReceiverInfo = {
  name: string;
  lastName: string;
  fullName: string;
  dni: string;
  relationship: string;
};

export function getReceiverInfo(
  dt: Record<string, unknown>,
  relationshipTagToLabel: (tag: string) => string
): ReceiverInfo | null {
  const pod = getProofFromDetail(dt);
  if (!pod) return null;
  const name = (pick<string>(pod, "name") ?? "").trim() || "-";
  const lastName = (pick<string>(pod, "lastName", "last_name") ?? "").trim() || "-";
  const fullName = [name, lastName].filter((x) => x !== "-").join(" ") || "-";
  const dni = (pick<string>(pod, "identificationNumber", "identification_number") ?? "").trim() || "-";
  const relationshipTag = (pick<string>(pod, "relationship") ?? "").trim();
  const relationship = relationshipTag ? relationshipTagToLabel(relationshipTag) : "-";
  return { name, lastName, fullName, dni, relationship };
}

export function getProofThumbnails(dt: Record<string, unknown>): ProofOfDeliveryItemType[] {
  const fromDetail = getProofOfDeliveryArray(dt);
  if (fromDetail.length > 0) return fromDetail;
  const history = (dt.transactionHistory ?? []) as unknown[];
  const deliveredStep = [...history]
    .reverse()
    .find((trx: unknown) => {
      const arr = getProofOfDeliveryArray(trx);
      return (trx as Record<string, unknown>)?.reason === "state_delivered" && arr.length > 0;
    });
  return deliveredStep ? getProofOfDeliveryArray(deliveredStep) : [];
}
